export const WORKFLOW_IDS = {
  FETCH_QUOTE: 'fetch.quote',
  FETCH_SELECT: 'fetch.select',
  FETCH_CUSTOMER: 'fetch.customer',
  FETCH_EVENTS: 'fetch.events',
} as const;

export const APP_NAME = 'fetch-pet-insurance';
export const APP_VERSION = '1.0.0';

export const SDK_TAG = 'payForm';

export const SDK_ERROR_CODES = {
  TAG_NOT_PROVIDED: 'E4101',
  TAG_NOT_FOUND: 'E4102',
  PROSPECT_ID_MISSING: 'E4103',
  BEARER_TOKEN_MISSING: 'E4104',
  PAYMENT_CANCELLED: 'E4105',
  JS_ERROR: 'E4106',
  BEARER_TOKEN_INVALID: 'E4107',
} as const;

export const IFRAME_SIZES = {
  desktop: { width: 800, height: 650 },
  tablet: { width: 600, height: 750 },
  mobile: { width: '100%', height: 850 },
} as const;

const SDK_SCRIPT_URLS = {
  int2: 'https://int2-salesfunnelsdk.fetchplatform.com/fetchWidget/0.1.0/sdk.js',
  qa2: 'https://qa2-salesfunnelsdk.fetchplatform.com/fetchWidget/0.1.0/sdk.js',
  prod: 'https://salesfunnelsdk.fetchplatform.com/fetchWidget/0.1.0/sdk.js',
} as const;

// The clickout's utm params now come from apps-gateway, which builds that whole
// URL. These are only for the pre-quote welcome link below, and are kept in step
// with the gateway's `NARVAR_CAMPAIGN` so both landings attribute the same way.
const NARVAR_UTM = {
  utm_medium: 'partnership',
  utm_source: 'narvar',
  utm_campaign: 'narvar_api',
} as const;

// Where "See what's covered" points, quote or no quote.
export const FETCH_WELCOME_URL = `https://www.fetchpet.com/welcome?${new URLSearchParams(
  { ...NARVAR_UTM, p: 'narvar' },
).toString()}`;

export const PET_AGE_OPTIONS = [
  // Fetch takes "0" for a pet under a year old.
  { value: '0', label: '6 weeks to 11 months' },
  ...Array.from({ length: 8 }, (_, i) => ({
    value: String(i + 1),
    label: `${i + 1} ${i === 0 ? 'year' : 'years'} old`,
  })),
] as const;

function isProdStack(): boolean {
  return (process.env.NEXT_PUBLIC_STACK ?? '').toLowerCase().startsWith('prod');
}

export function getFetchSdkUrl(): string {
  return isProdStack() ? SDK_SCRIPT_URLS.prod : SDK_SCRIPT_URLS.int2;
}

interface FetchBuyUrlArgs {
  buyUrl?: string;
  deviceType?: string;
}

// The gateway owns the path and params now, but the host stays ours to vouch
// for. fetchQuote casts the quote response with no schema validation, and
// index.tsx assigns this to the shopper's own tab when the popup is blocked --
// so without a list the navigation target is whatever host that JSON carried.
// Building the URL locally used to make these two the only reachable hosts;
// this keeps that guarantee. A host Fetch adds later has to be added here,
// which degrades to the error phase rather than navigating somewhere unvetted.
const ALLOWED_BUY_URL_HOSTS = new Set([
  'int2-salesfunnel2.fetchplatform.com',
  'www.fetchpet.com',
]);

// apps-gateway builds the clickout URL now, so this only validates what it sent
// and stamps the device on it. An unusable URL returns undefined rather than a
// broken link, and the caller keeps the shopper on the plan screen.
export function getFetchBuyUrl({
  buyUrl,
  deviceType,
}: FetchBuyUrlArgs): string | undefined {
  if (!buyUrl) return undefined;

  let url: URL;
  try {
    url = new URL(buyUrl);
  } catch {
    return undefined;
  }
  if (url.protocol !== 'https:') return undefined;
  if (!ALLOWED_BUY_URL_HOSTS.has(url.hostname)) return undefined;

  if (deviceType) url.searchParams.set('deviceType', deviceType);
  return url.toString();
}
